import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Home() {
  const { isAdmin } = useAuth();
  const [site, setSite] = useState(null);
  const [layers, setLayers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data));
    api.get("/layers").then((r) => setLayers(r.data));
  }, []);

  const startEdit = () => {
    setDraft({ site: { ...site }, layers: layers.map((l) => ({ ...l })) });
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(null);
  };
  const save = async () => {
    setSaving(true);
    try {
      const [siteRes, layersRes] = await Promise.all([
        api.put("/site", draft.site),
        api.put("/layers", { layers: draft.layers }),
      ]);
      setSite(siteRes.data);
      setLayers(layersRes.data);
      setEditing(false);
      toast.success("Manuscript & strata updated.");
    } catch (e) {
      toast.error("Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const updateLayer = (idx, key, val) => {
    setDraft((d) => ({
      ...d,
      layers: d.layers.map((l, i) => (i === idx ? { ...l, [key]: val } : l)),
    }));
  };

  if (!site || layers.length === 0) {
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: loading manuscript :::</div>;
  }

  const display = editing ? draft.site : site;
  const layerView = editing ? draft.layers : layers;

  return (
    <div className="space-y-20" data-testid="home-page">
      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-7 space-y-8">
          <div className="flex items-center gap-3 text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
            <span>Dossier 00 — Prima Descensio</span>
            <span className="h-px flex-1 bg-[#333]" />
            <span>Anno Domini MMXXVI</span>
          </div>

          {editing ? (
            <>
              <input
                className="tiv-input text-2xl"
                value={display.intro_title}
                onChange={(e) => setDraft({ ...draft, site: { ...draft.site, intro_title: e.target.value } })}
                data-testid="home-edit-title"
              />
              <input
                className="tiv-input"
                value={display.intro_subtitle}
                onChange={(e) => setDraft({ ...draft, site: { ...draft.site, intro_subtitle: e.target.value } })}
                data-testid="home-edit-subtitle"
              />
              <textarea
                className="tiv-textarea"
                value={display.intro_body}
                onChange={(e) => setDraft({ ...draft, site: { ...draft.site, intro_body: e.target.value } })}
                data-testid="home-edit-body"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  className="tiv-input"
                  value={display.contact_email}
                  onChange={(e) => setDraft({ ...draft, site: { ...draft.site, contact_email: e.target.value } })}
                  data-testid="home-edit-email"
                />
                <input
                  className="tiv-input"
                  value={display.contact_phone}
                  onChange={(e) => setDraft({ ...draft, site: { ...draft.site, contact_phone: e.target.value } })}
                  data-testid="home-edit-phone"
                />
              </div>
            </>
          ) : (
            <>
              <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                {site.intro_title}
              </h1>
              <p className="text-sm tracking-[0.35em] uppercase text-[#A3A3A3]">
                {site.intro_subtitle}
              </p>
              <p className="text-base leading-relaxed text-[#E3E1D9] max-w-2xl whitespace-pre-wrap">
                {site.intro_body}
              </p>
              <div className="flex flex-wrap gap-4 items-center pt-2">
                <a href="/archive" className="tiv-btn" data-testid="home-cta-archive">
                  Open Archive ↘
                </a>
                <a href="/entities" className="tiv-btn" data-testid="home-cta-entities">
                  Catalogue Entities
                </a>
                <a href="/realities" className="tiv-btn" data-testid="home-cta-realities">
                  Realities Index
                </a>
                {isAdmin && (
                  <button className="tiv-btn" onClick={startEdit} data-testid="home-edit-btn">
                    Edit Manuscript & Strata
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <aside className="lg:col-span-5 border border-[#333] p-6 sm:p-8 space-y-4 bg-[#0a0a0a]">
          <div className="section-label">
            <span>Manifest · Sigil</span>
            <span className="tiv-stamp">CLASSIFIED</span>
          </div>
          <div className="font-mono-data text-xs space-y-3 text-[#A3A3A3]">
            <div className="flex justify-between"><span>Designation</span><span className="text-[#E3E1D9]">T.I.V. NEXUS</span></div>
            <div className="flex justify-between"><span>Order</span><span className="text-[#E3E1D9]">Septem Profunda</span></div>
            <div className="flex justify-between"><span>Origin</span><span className="text-[#E3E1D9]">Terra · Surface</span></div>
            <div className="flex justify-between"><span>Folders</span><span className="text-[#E3E1D9]">A-000 — A-100</span></div>
            <div className="flex justify-between"><span>Correspondence</span><span className="text-[#E3E1D9]">{display.contact_email}</span></div>
            <div className="flex justify-between"><span>Channel</span><span className="text-[#E3E1D9]">{display.contact_phone}</span></div>
          </div>
          <div className="pt-4 border-t border-[#333] text-[0.65rem] leading-relaxed text-[#555]">
            “Per septem descensus, veritas. Per silentium, custos.”
          </div>
        </aside>
      </section>

      {/* Layer index */}
      <section data-testid="layers-section">
        <div className="section-label">
          <span>The Seven Layers · Index Descensus</span>
          <span>VII · STRATA</span>
        </div>

        {editing ? (
          <div className="space-y-3 border border-[#333] p-5 bg-[#0a0a0a]">
            <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555]">
              Editing the seven strata · order matters
            </div>
            {draft.layers.map((l, i) => (
              <div key={l.roman} className="grid grid-cols-12 gap-2" data-testid={`layer-edit-row-${i}`}>
                <input className="tiv-input col-span-1" value={l.roman} onChange={(e) => updateLayer(i, "roman", e.target.value)} />
                <input className="tiv-input col-span-3" value={l.name} onChange={(e) => updateLayer(i, "name", e.target.value)} placeholder="lore name" />
                <input className="tiv-input col-span-2" value={l.english} onChange={(e) => updateLayer(i, "english", e.target.value)} placeholder="english" />
                <input className="tiv-input col-span-6" value={l.sin} onChange={(e) => updateLayer(i, "sin", e.target.value)} placeholder="sin description" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 border-t border-l border-[#333]">
            {layerView.map((l) => (
              <Link
                key={l.roman}
                to={`/strata/${l.roman.toLowerCase()}`}
                className="block border-r border-b border-[#333] p-5 hover:bg-[#0f0f0f] transition-colors cursor-pointer group"
                data-testid={`layer-${l.roman.toLowerCase()}`}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <div className="text-[0.6rem] tracking-[0.4em] text-[#555] uppercase">
                    Stratum {l.roman}
                  </div>
                  <span className="text-[0.55rem] tracking-[0.25em] uppercase text-[#555] group-hover:text-[#E3E1D9]">
                    open →
                  </span>
                </div>
                <div className="font-serif-display text-2xl mb-1">{l.name}</div>
                <div className="text-[0.55rem] tracking-[0.3em] uppercase text-[#555] mb-2">
                  {l.english}
                </div>
                <div className="text-[0.7rem] leading-relaxed text-[#A3A3A3]">{l.sin}</div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {editing && (
        <div className="flex gap-3 border-t border-[#333] pt-8 sticky bottom-4 bg-[#050505] p-4 border border-[#333] z-30">
          <button className="tiv-btn" onClick={save} disabled={saving} data-testid="home-save-btn">
            {saving ? "Sealing..." : "Seal & Save"}
          </button>
          <button className="tiv-btn" onClick={cancel} data-testid="home-cancel-btn">
            Discard
          </button>
        </div>
      )}

      {/* Doctrine note */}
      <section className="border-t border-[#333] pt-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4 section-label">
          <span>Notice</span>
          <span>§ 01</span>
        </div>
        <div className="md:col-span-8 text-sm leading-relaxed text-[#E3E1D9] space-y-4">
          <p>
            What follows is not entertainment. It is a record. Each folder bears the residue of a
            descent — coordinates, testimony, names struck through. Some pages are <span className="tiv-redact">[redacted]</span> for the
            safety of the surface. Some are <span className="status-pill status-lost">LOST</span> entirely.
          </p>
          <p className="text-[#A3A3A3]">
            Should you find this archive, read carefully. Do not speak the names aloud.
          </p>
        </div>
      </section>
    </div>
  );
}
